
import { useState, useMemo } from 'react';
import { Search, Check, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useProviderData } from '@/components/admin/peo/hooks/useProviderData';
import { simplifyProviderName } from '@/components/admin/peo/utils/nameUtils';
import type { PEOProvider } from '@/types/peo.types';

interface PEOProviderSelectorProps {
  preferredProviders: string[];
  excludedProviders: string[];
  onTogglePreferred: (providerName: string) => void;
  onToggleExcluded: (providerName: string) => void;
}

export const PEOProviderSelector = ({
  preferredProviders = [],
  excludedProviders = [],
  onTogglePreferred,
  onToggleExcluded,
}: PEOProviderSelectorProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const { providers, isLoading } = useProviderData();

  const filteredProviders = useMemo(() => {
    if (!providers || !searchTerm.trim()) return [];
    
    return providers.filter(provider => 
      provider.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      provider.specialties?.some(specialty => 
        specialty.toLowerCase().includes(searchTerm.toLowerCase())
      ) ||
      provider.states_served?.some(state => 
        state.toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [providers, searchTerm]);

  const selectedProviders = useMemo(() => {
    if (!providers) return [];
    
    const allSelectedNames = [...preferredProviders, ...excludedProviders];
    return providers.filter(provider => allSelectedNames.includes(provider.name));
  }, [providers, preferredProviders, excludedProviders]);

  const isPreferred = (providerName: string) => preferredProviders.includes(providerName);
  const isExcluded = (providerName: string) => excludedProviders.includes(providerName);

  const handleTogglePreferred = (providerName: string) => {
    // If already excluded, remove from excluded first
    if (isExcluded(providerName)) {
      onToggleExcluded(providerName);
    }
    onTogglePreferred(providerName);
  };

  const handleToggleExcluded = (providerName: string) => {
    // If already preferred, remove from preferred first
    if (isPreferred(providerName)) {
      onTogglePreferred(providerName);
    }
    onToggleExcluded(providerName);
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-10 bg-muted rounded animate-pulse" />
        {Array(3).fill(0).map((_, i) => (
          <div key={i} className="h-16 bg-muted rounded animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Search Bar */}
      <div className="relative">
        <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search PEO providers by name, specialty, or state..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Search Results - Only show when searching */}
      {searchTerm.trim() && (
        <div className="space-y-3">
          <h3 className="text-sm font-medium text-muted-foreground">Search Results</h3>
          <div className="max-h-60 overflow-y-auto space-y-2">
            {filteredProviders.length === 0 ? (
              <div className="text-center py-4 text-muted-foreground text-sm">
                No providers found matching your search.
              </div>
            ) : (
              filteredProviders.map((provider) => (
                <ProviderSelectionCard
                  key={provider.id}
                  provider={provider}
                  isPreferred={isPreferred(provider.name)}
                  isExcluded={isExcluded(provider.name)}
                  onTogglePreferred={() => handleTogglePreferred(provider.name)}
                  onToggleExcluded={() => handleToggleExcluded(provider.name)}
                />
              ))
            )}
          </div>
        </div>
      )}

      {/* Selected Providers - Always show if any are selected */}
      {selectedProviders.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-medium text-muted-foreground">Selected Providers</h3>
          <div className="max-h-60 overflow-y-auto space-y-2">
            {selectedProviders.map((provider) => (
              <ProviderSelectionCard
                key={provider.id}
                provider={provider}
                isPreferred={isPreferred(provider.name)}
                isExcluded={isExcluded(provider.name)}
                onTogglePreferred={() => handleTogglePreferred(provider.name)}
                onToggleExcluded={() => handleToggleExcluded(provider.name)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

interface ProviderSelectionCardProps {
  provider: PEOProvider;
  isPreferred: boolean;
  isExcluded: boolean;
  onTogglePreferred: () => void;
  onToggleExcluded: () => void;
}

const ProviderSelectionCard = ({
  provider,
  isPreferred,
  isExcluded,
  onTogglePreferred,
  onToggleExcluded,
}: ProviderSelectionCardProps) => {
  const displayName = simplifyProviderName(provider.name);
  
  return (
    <Card className={`transition-all duration-200 ${
      isPreferred ? 'border-green-500 bg-green-50' : 
      isExcluded ? 'border-red-500 bg-red-50' : 
      'border-border hover:shadow-md'
    }`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="font-medium text-sm truncate">{displayName}</h3>
              {isPreferred && (
                <Badge className="bg-green-500 text-white">Preferred</Badge>
              )}
              {isExcluded && (
                <Badge variant="destructive">Excluded</Badge>
              )}
            </div>
          </div>
          
          <div className="flex gap-2 ml-4">
            <Button
              variant="outline"
              size="sm"
              onClick={onTogglePreferred}
              className={`${isPreferred 
                ? "bg-green-500 hover:bg-green-600 text-white border-green-500" 
                : "border-green-500 text-green-600 hover:bg-green-50"
              }`}
            >
              <Check className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onToggleExcluded}
              className={`${isExcluded 
                ? "bg-red-500 hover:bg-red-600 text-white border-red-500" 
                : "border-red-500 text-red-600 hover:bg-red-50"
              }`}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
